// Shopify Storefront Cart API — persistent cart mutations and queries.
// The STOREFRONT_TOKEN is the public Storefront Access Token: safe in client code.
// All prices come from Shopify's response — never calculated on the frontend.

const STOREFRONT_DOMAIN = 'xic12r-zi.myshopify.com';
const STOREFRONT_TOKEN = '18c5e60b188a9e01c64028e2264bd157';
const STOREFRONT_API = `https://${STOREFRONT_DOMAIN}/api/2024-01/graphql.json`;

async function gql(query, variables = {}) {
  const res = await fetch(STOREFRONT_API, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': STOREFRONT_TOKEN,
    },
    body: JSON.stringify({ query, variables }),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const json = await res.json();
  if (json.errors) throw new Error(json.errors[0].message);
  return json;
}

// Shared cart field fragment — used in every mutation and the query.
const CART_FIELDS = `
  id
  checkoutUrl
  lines(first: 50) {
    edges {
      node {
        id
        quantity
        merchandise {
          ... on ProductVariant {
            id
            title
            weight
            weightUnit
            product {
              id
              title
            }
          }
        }
        attributes {
          key
          value
        }
        cost {
          totalAmount {
            amount
            currencyCode
          }
        }
      }
    }
  }
  cost {
    subtotalAmount {
      amount
      currencyCode
    }
  }
`;

/**
 * Creates a new Shopify cart.
 * lines: [{ merchandiseId, quantity, attributes? }]
 * Returns the cart object or throws.
 */
export async function cartCreate(lines) {
  const data = await gql(`
    mutation CartCreate($input: CartInput!) {
      cartCreate(input: $input) {
        cart { ${CART_FIELDS} }
        userErrors { field message }
      }
    }
  `, { input: { lines } });
  const result = data?.data?.cartCreate;
  if (result?.userErrors?.length) throw new Error(result.userErrors[0].message);
  if (!result?.cart) throw new Error('Cart create returned no cart');
  return result.cart;
}

/**
 * Adds lines to an existing cart.
 * lines: [{ merchandiseId, quantity, attributes? }]
 */
export async function cartLinesAdd(cartId, lines) {
  const data = await gql(`
    mutation CartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
      cartLinesAdd(cartId: $cartId, lines: $lines) {
        cart { ${CART_FIELDS} }
        userErrors { field message }
      }
    }
  `, { cartId, lines });
  const result = data?.data?.cartLinesAdd;
  if (result?.userErrors?.length) throw new Error(result.userErrors[0].message);
  if (!result?.cart) throw new Error('Cart lines add returned no cart');
  return result.cart;
}

/**
 * Updates quantities on existing cart lines.
 * lines: [{ id: lineId, quantity }]
 */
export async function cartLinesUpdate(cartId, lines) {
  const data = await gql(`
    mutation CartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
      cartLinesUpdate(cartId: $cartId, lines: $lines) {
        cart { ${CART_FIELDS} }
        userErrors { field message }
      }
    }
  `, { cartId, lines });
  const result = data?.data?.cartLinesUpdate;
  if (result?.userErrors?.length) throw new Error(result.userErrors[0].message);
  if (!result?.cart) throw new Error('Cart lines update returned no cart');
  return result.cart;
}

/**
 * Removes lines from the cart by line ID.
 * lineIds: [string]
 */
export async function cartLinesRemove(cartId, lineIds) {
  const data = await gql(`
    mutation CartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
      cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
        cart { ${CART_FIELDS} }
        userErrors { field message }
      }
    }
  `, { cartId, lineIds });
  const result = data?.data?.cartLinesRemove;
  if (result?.userErrors?.length) throw new Error(result.userErrors[0].message);
  if (!result?.cart) throw new Error('Cart lines remove returned no cart');
  return result.cart;
}

/**
 * Fetches the full cart state. Returns null if the cart no longer exists.
 */
export async function cartQuery(cartId) {
  const data = await gql(`
    query CartQuery($cartId: ID!) {
      cart(id: $cartId) {
        ${CART_FIELDS}
      }
    }
  `, { cartId });
  return data?.data?.cart ?? null;
}
